import { useState, useCallback } from 'react';
import { Header, Sidebar, Footer } from './components/Layout';
import { Modal } from './components/UI/Modal';
import { FullscreenExitButton } from './components/UI/FullscreenExitButton';
import { AFFILIATE_PRODUCTS, amazonSearchUrl } from './components/Layout/AffiliateAd';
import {
  DeadPixelTest,
  UniformityTest,
  ColorGradientTest,
  ResponseTimeTest,
  ContrastTest,
  BrightnessTest,
  TextClarityTest,
  ViewingAngleTest,
  BurnInFixTest,
} from './components/Tests';
import { useFullscreen } from './hooks/useFullscreen';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useTestState } from './hooks/useTestState';
import { TestType } from './types/test';
import { DEAD_PIXEL_COLORS } from './utils/constants';
import './styles/globals.css';

export default function App() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const { isFullscreen, toggleFullscreen } = useFullscreen();
  const {
    selectedTest,
    setSelectedTest,
    isPaused,
    togglePause,
    config,
    updateConfig,
    resetConfig,
  } = useTestState();

  const handleNext = useCallback(() => {
    if (selectedTest === 'dead-pixel') {
      const nextIndex = (config.deadPixel.currentColorIndex + 1) % DEAD_PIXEL_COLORS.length;
      updateConfig('deadPixel', { currentColorIndex: nextIndex });
    }
  }, [selectedTest, config.deadPixel.currentColorIndex, updateConfig]);

  const handlePrev = useCallback(() => {
    if (selectedTest === 'dead-pixel') {
      const prevIndex = (config.deadPixel.currentColorIndex - 1 + DEAD_PIXEL_COLORS.length) % DEAD_PIXEL_COLORS.length;
      updateConfig('deadPixel', { currentColorIndex: prevIndex });
    }
  }, [selectedTest, config.deadPixel.currentColorIndex, updateConfig]);

  const handleIncrease = useCallback(() => {
    if (selectedTest === 'response-time') {
      const newSpeed = Math.max(250, config.responseTime.speed - 250);
      updateConfig('responseTime', { speed: newSpeed });
    }
  }, [selectedTest, config.responseTime.speed, updateConfig]);

  const handleDecrease = useCallback(() => {
    if (selectedTest === 'response-time') {
      const newSpeed = Math.min(2000, config.responseTime.speed + 250);
      updateConfig('responseTime', { speed: newSpeed });
    }
  }, [selectedTest, config.responseTime.speed, updateConfig]);

  const handleReset = useCallback(() => {
    const testKeyMap: Record<TestType, keyof typeof config> = {
      'dead-pixel': 'deadPixel',
      'uniformity': 'uniformity',
      'gradient': 'gradient',
      'response-time': 'responseTime',
      'contrast': 'contrast',
      'brightness': 'brightness',
      'text-clarity': 'textClarity',
      'viewing-angle': 'viewingAngle',
      'burn-in-fix': 'burnInFix',
    };
    resetConfig(testKeyMap[selectedTest]);
  }, [selectedTest, resetConfig]);

  useKeyboardShortcuts({
    onTestChange: setSelectedTest,
    onToggleFullscreen: toggleFullscreen,
    onTogglePause: togglePause,
    onReset: handleReset,
    onShowHelp: () => setShowHelpModal(true),
    onNext: handleNext,
    onPrev: handlePrev,
    onIncrease: handleIncrease,
    onDecrease: handleDecrease,
  });

  const renderTest = () => {
    switch (selectedTest) {
      case 'dead-pixel':
        return (
          <DeadPixelTest
            currentColorIndex={config.deadPixel.currentColorIndex}
            autoMode={config.deadPixel.autoMode}
            interval={config.deadPixel.interval}
            isPaused={isPaused}
            customColor={config.deadPixel.customColor}
            useCustomColor={config.deadPixel.useCustomColor}
            onColorChange={(index) => updateConfig('deadPixel', { currentColorIndex: index })}
            onAutoModeChange={(auto) => updateConfig('deadPixel', { autoMode: auto })}
            onIntervalChange={(interval) => updateConfig('deadPixel', { interval })}
            onCustomColorChange={(customColor) => updateConfig('deadPixel', { customColor })}
            onUseCustomColorChange={(useCustomColor) => updateConfig('deadPixel', { useCustomColor })}
          />
        );
      case 'uniformity':
        return (
          <UniformityTest
            color={config.uniformity.color}
            brightness={config.uniformity.brightness}
            gridEnabled={config.uniformity.gridEnabled}
            gridSize={config.uniformity.gridSize}
            onColorChange={(color) => updateConfig('uniformity', { color })}
            onBrightnessChange={(brightness) => updateConfig('uniformity', { brightness })}
            onGridEnabledChange={(gridEnabled) => updateConfig('uniformity', { gridEnabled })}
            onGridSizeChange={(gridSize) => updateConfig('uniformity', { gridSize })}
          />
        );
      case 'gradient':
        return (
          <ColorGradientTest
            direction={config.gradient.direction}
            steps={config.gradient.steps}
            preset={config.gradient.preset}
            onDirectionChange={(direction) => updateConfig('gradient', { direction })}
            onStepsChange={(steps) => updateConfig('gradient', { steps })}
            onPresetChange={(preset) => updateConfig('gradient', { preset })}
          />
        );
      case 'response-time':
        return (
          <ResponseTimeTest
            speed={config.responseTime.speed}
            shape={config.responseTime.shape}
            direction={config.responseTime.direction}
            showTrail={config.responseTime.showTrail}
            isPaused={isPaused}
            objectColor={config.responseTime.objectColor}
            onSpeedChange={(speed) => updateConfig('responseTime', { speed })}
            onShapeChange={(shape) => updateConfig('responseTime', { shape })}
            onDirectionChange={(direction) => updateConfig('responseTime', { direction })}
            onShowTrailChange={(showTrail) => updateConfig('responseTime', { showTrail })}
            onObjectColorChange={(objectColor) => updateConfig('responseTime', { objectColor })}
          />
        );
      case 'contrast':
        return (
          <ContrastTest
            gridSize={config.contrast.gridSize}
            onGridSizeChange={(gridSize) => updateConfig('contrast', { gridSize })}
          />
        );
      case 'brightness':
        return (
          <BrightnessTest
            brightness={config.brightness.brightness}
            windowSize={config.brightness.windowSize}
            onBrightnessChange={(brightness) => updateConfig('brightness', { brightness })}
            onWindowSizeChange={(windowSize) => updateConfig('brightness', { windowSize })}
          />
        );
      case 'text-clarity':
        return (
          <TextClarityTest
            fontSize={config.textClarity.fontSize}
            fontFamily={config.textClarity.fontFamily}
            letterSpacing={config.textClarity.letterSpacing}
            lineHeight={config.textClarity.lineHeight}
            onFontSizeChange={(fontSize) => updateConfig('textClarity', { fontSize })}
            onFontFamilyChange={(fontFamily) => updateConfig('textClarity', { fontFamily })}
            onLetterSpacingChange={(letterSpacing) => updateConfig('textClarity', { letterSpacing })}
            onLineHeightChange={(lineHeight) => updateConfig('textClarity', { lineHeight })}
          />
        );
      case 'viewing-angle':
        return (
          <ViewingAngleTest
            pattern={config.viewingAngle.pattern}
            showIndicators={config.viewingAngle.showIndicators}
            onPatternChange={(pattern) => updateConfig('viewingAngle', { pattern })}
            onShowIndicatorsChange={(showIndicators) => updateConfig('viewingAngle', { showIndicators })}
          />
        );
      case 'burn-in-fix':
        return (
          <BurnInFixTest
            settings={config.burnInFix}
            onSettingsChange={(settings) => updateConfig('burnInFix', settings)}
          />
        );
    }
  };

  return (
    <div className="min-h-screen text-white" style={{ background: '#0a0a0f' }}>
      {!isFullscreen && (
        <>
          <Header
            onShowHelp={() => setShowHelpModal(true)}
            onToggleFullscreen={() => toggleFullscreen()}
            onToggleMobileMenu={() => setMobileMenuOpen((o) => !o)}
          />
          <Sidebar
            selectedTest={selectedTest}
            onSelectTest={setSelectedTest}
            isCollapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            mobileOpen={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
          />
          {/* Mobile drawer backdrop */}
          {mobileMenuOpen && (
            <div
              className="md:hidden fixed left-0 right-0 bottom-0 top-[60px] z-[55] bg-black/50 animate-fade-in"
              onClick={() => setMobileMenuOpen(false)}
            />
          )}
          <Footer selectedTest={selectedTest} isPaused={isPaused} />
        </>
      )}

      <main
        className={`transition-all duration-300 ${
          isFullscreen
            ? 'fixed inset-0'
            : `pt-[60px] pb-[38px] pl-0 ${sidebarCollapsed ? 'md:pl-[68px]' : 'md:pl-[272px]'}`
        }`}
      >
        <div className={`${isFullscreen ? 'h-full' : 'h-[calc(100vh-98px)]'}`}>
          {renderTest()}
        </div>
      </main>

      {isFullscreen && <FullscreenExitButton onExit={() => toggleFullscreen()} />}

      <Modal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title="OLED Tools — Help"
        size="xl"
      >
        <div className="space-y-5 text-gray-300">

          {/* ── Keyboard shortcuts ──────────────────────────── */}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-widest text-white/30 mb-3">Keyboard Shortcuts</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              {[
                ['1–9', 'Switch between tests'],
                ['F / F11', 'Toggle fullscreen'],
                ['Space', 'Pause / Resume'],
                ['R', 'Reset current test'],
                ['← →', 'Navigate options'],
                ['+  −', 'Adjust speed'],
                ['?', 'Show this panel'],
              ].map(([key, desc]) => (
                <div key={key} className="flex items-center gap-2 py-0.5">
                  <kbd className="px-2 py-0.5 rounded-lg text-[11px] font-mono text-white/70 flex-shrink-0"
                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    {key}
                  </kbd>
                  <span className="text-white/50 text-xs">{desc}</span>
                </div>
              ))}
            </div>
          </section>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} />

          {/* ── What each test does ─────────────────────────── */}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-widest text-white/30 mb-3">How Each Test Works</h3>
            <div className="space-y-3">
              {[
                {
                  key: '1', icon: '🔴', name: 'Dead Pixel',
                  body: 'Cycles through solid red, green, blue, white, black, cyan, magenta, and yellow fills. Each color isolates one or more subpixel channels — a dead pixel appears as a fixed black dot on every color; a stuck pixel shows as a fixed color dot that never changes.',
                },
                {
                  key: '2', icon: '⬜', name: 'Uniformity',
                  body: 'Fills the screen with a solid color at adjustable brightness. Use this to spot backlight bleed (bright patches near corners/edges on LCD), IPS glow (a milky haze at off-axis angles), or OLED panel luminance inconsistency. Enable the grid overlay to divide the panel into zones for systematic inspection.',
                },
                {
                  key: '3', icon: '🌈', name: 'Color Gradient',
                  body: 'Renders smooth multi-step gradients across the full screen. Color banding — visible as hard stripes instead of a continuous ramp — indicates an 8-bit FRC panel or poor dithering. True 10-bit panels produce near-invisible transitions. Increase the step count for a stricter test.',
                },
                {
                  key: '4', icon: '🚀', name: 'Response Time',
                  body: 'Animates a solid shape across a contrasting background. Ghosting (a faded trail behind the object) reveals slow pixel response times (common on IPS/VA). Inverse ghosting (a bright halo ahead of the object) is overshoot from aggressive overdrive. Adjust speed to find the threshold where artifacts appear.',
                },
                {
                  key: '5', icon: '◼️', name: 'Contrast',
                  body: 'Displays alternating black-and-white patterns. On OLED panels blacks should be a true, absolute black (infinite contrast); on LCD panels blacks lift to dark gray. If you see halation (white bleeding into adjacent dark areas), your local-dimming zone resolution is limited.',
                },
                {
                  key: '6', icon: '☀️', name: 'Brightness',
                  body: 'Tests peak brightness under different Average Picture Levels (APL). OLED panels throttle brightness as more screen area is lit — a small white window on a black background will peak much higher (e.g. 1000 nits) than a full-white screen (e.g. 200 nits). Use this to observe your panel\'s ABL (Automatic Brightness Limiting) behavior.',
                },
                {
                  key: '7', icon: '📝', name: 'Text Clarity',
                  body: 'Renders text at multiple fonts, sizes, and spacings. Use this to evaluate subpixel rendering (RGB stripe vs PenTile), font hinting quality, and antialiasing smoothness. Critical for reading-heavy use cases like coding or e-books. Fine sizes (10–14 pt) expose poor hinting the most.',
                },
                {
                  key: '8', icon: '👁️', name: 'Viewing Angle',
                  body: 'Displays structured gradient and color patterns to reveal off-axis shifts. TN panels shift dramatically (colors invert at steep angles); IPS panels shift moderately; OLED and VA have their own characteristics. Walk around your display and observe color/contrast consistency.',
                },
                {
                  key: '9', icon: '🛡️', name: 'Burn-In Prevention',
                  body: 'Runs continuous animated patterns that keep every pixel changing constantly. Critical for OLED and AMOLED panels: organic compounds degrade unevenly if a static element (taskbar, dock, watermark, HUD) is displayed at fixed brightness for extended periods. The pixel-shift and plasma patterns are the most effective. Run for 30–90 minutes on at-risk displays. Works on LCD/LED too — any panel benefits from periodic full-pixel exercise to keep backlight uniformity even.',
                },
              ].map((t) => (
                <div key={t.key} className="flex gap-3 p-3 rounded-xl"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div className="flex-shrink-0 flex flex-col items-center gap-1">
                    <span className="text-base">{t.icon}</span>
                    <kbd className="text-[9px] font-mono text-white/25 px-1 py-0.5 rounded"
                      style={{ background: 'rgba(255,255,255,0.06)' }}>{t.key}</kbd>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-white/80 mb-0.5">{t.name}</p>
                    <p className="text-[11px] text-white/40 leading-relaxed">{t.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} />

          {/* ── Using on a TV ───────────────────────────────── */}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-widest text-white/30 mb-3">Using on a TV Screen</h3>
            <div className="flex gap-3 p-3 rounded-xl"
              style={{ background: 'rgba(0,217,255,0.04)', border: '1px solid rgba(0,217,255,0.1)' }}>
              <span className="text-2xl flex-shrink-0">📺</span>
              <div className="text-[11px] text-white/50 leading-relaxed space-y-1.5">
                <p>You can run OLED Tools on any TV or large display directly from a laptop or PC:</p>
                <ol className="space-y-1 list-decimal list-inside text-white/40">
                  <li>Connect your laptop to the TV using an <span className="text-white/60">HDMI or DisplayPort cable</span>.</li>
                  <li>Set the TV as your primary or extended display in your OS display settings.</li>
                  <li>Open a browser on the TV screen and navigate to <span className="text-white/60">OLEDTools.com</span>.</li>
                  <li>Select <span className="text-white/60">Burn-In Prevention</span> from the sidebar and press the fullscreen button.</li>
                  <li>Leave it running for <span className="text-white/60">30–90 minutes</span> to exercise OLED subpixels and reduce image retention.</li>
                </ol>
                <p className="text-white/30 mt-1">Works on OLED TVs (LG, Sony, Samsung QD-OLED), LED/LCD TVs, and any HDMI-connected display.</p>
              </div>
            </div>
          </section>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} />

          {/* ── Affiliate ads ───────────────────────────────── */}
          <section>
            <p className="text-[9px] font-bold uppercase tracking-widest text-white/20 mb-2">Sponsored — Recommended Gear</p>
            <div className="grid grid-cols-2 gap-2">
              {AFFILIATE_PRODUCTS.map((p) => (
                <a
                  key={p.query}
                  href={amazonSearchUrl(p.query)}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  className="flex items-start gap-2.5 p-3 rounded-xl transition-all duration-200 group"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,217,255,0.25)';
                    (e.currentTarget as HTMLElement).style.background = 'rgba(0,217,255,0.05)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.06)';
                    (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)';
                  }}
                >
                  <span className="text-xl flex-shrink-0">{p.icon}</span>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-white/70 group-hover:text-white transition-colors leading-snug">{p.name}</p>
                    <p className="text-[10px] text-white/30 mt-0.5">{p.desc}</p>
                    <p className="text-[10px] text-[#00d9ff]/50 group-hover:text-[#00d9ff] transition-colors mt-1">View on Amazon →</p>
                  </div>
                </a>
              ))}
            </div>
          </section>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} />

          {/* ── Developer info & disclaimer ─────────────────── */}
          <section className="space-y-2">
            <p className="text-[10px] text-white/20 text-center">
              Built by <span className="text-white/35">Li Fan</span> — 2026
            </p>
            <p className="text-[10px] text-white/20 leading-relaxed text-center">
              OLED Tools is provided free of charge, as-is, with no warranty or guarantee of any kind.
              Results may vary by display model and panel condition. The burn-in prevention patterns
              are intended as a maintenance aid only — they cannot reverse severe organic degradation.
              Use at your own discretion.
            </p>
          </section>

        </div>
      </Modal>
    </div>
  );
}
